import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { bateriaApi, healthApi } from "../../api/endpoints.js";
import { AdultScreen, AdultHeader, Content } from "../../components/Layout.js";
import { Card } from "../../components/Card.js";
import { Button } from "../../components/Button.js";
import { Loader } from "../../components/Loader.js";
import { ErrorBanner } from "../../components/Form.js";
import { PeffPlayer } from "../../eval/PeffPlayer.js";
import { bateriaReanudable, planSesion } from "../../eval/bateria.js";
import { cargarApoyos } from "../../eval/usePeffPlayer.js";
import type { InitPlayerInput } from "../../eval/peff-player.js";
import type { BateriaCompletaDTO } from "../../api/types.js";
import { useAuth } from "../../auth/AuthContext.js";
import { useT } from "../../i18n/index.js";
import { mensajeDeError, useQuery } from "../../hooks/useQuery.js";
import styles from "./Parent.module.css";

type Fase = "prep" | "cargando" | "jugando";

/**
 * Checklist del adulto → creación de la batería PEFF → reproductor. La banda
 * etaria, el plan de ejercicios y la partición en sesiones los decide el API a
 * partir de la edad del niño (master §2.2): el cliente solo administra lo que
 * recibe. La prueba de micrófono es la primera pantalla del reproductor.
 */
export function StartEvalPage() {
  const t = useT();
  const navigate = useNavigate();
  const { rol } = useAuth();
  const { childId = "" } = useParams();
  // Destinos de navegación ROLE-AWARE: la pantalla es compartida padre/terapeuta.
  // El terapeuta vuelve SIEMPRE a la ficha del paciente (donde la batería recién
  // administrada aparece para procesar); el padre conserva su flujo original. Sin
  // esto, el terapeuta salía por rebote de guardas de rol a rutas de /padre.
  const esTerapeuta = rol === "terapeuta";
  const fichaTerapeuta = `/terapeuta/paciente/${childId}`;
  const exitTo = esTerapeuta ? fichaTerapeuta : "/padre";
  const doneTo = (bateriaId: string) =>
    esTerapeuta ? fichaTerapeuta : `/padre/reporte/${bateriaId}`;
  const backTo = esTerapeuta ? fichaTerapeuta : `/padre/nino/${childId}/consentimientos`;
  const [fase, setFase] = useState<Fase>("prep");
  const [error, setError] = useState<string | null>(null);
  const [bateria, setBateria] = useState<BateriaCompletaDTO | null>(null);
  const [plan, setPlan] = useState<InitPlayerInput | null>(null);
  const [modoPrueba, setModoPrueba] = useState(false);
  // Flag del entorno: solo si QA está habilitado aparece el toggle de prueba.
  const { data: health } = useQuery(() => healthApi.obtener(), []);
  const qaHabilitado = health?.qaModeEnabled === true;

  async function comenzar() {
    setError(null);
    setFase("cargando");
    try {
      // RETOMA la batería en curso si existe (clave de la 2.ª sesión de banda A);
      // solo crea una nueva si el niño no tiene ninguna abierta. Así la sesión 2
      // cae sobre la MISMA batería y esta puede llegar a 'completada'.
      const bateria = await resolverBateria(childId, qaHabilitado && modoPrueba);
      const apoyos = await cargarApoyos(bateria);
      const sesion = planSesion(bateria, apoyos);
      if (sesion.ejercicios.length === 0) {
        throw new Error("La batería no tiene ejercicios pendientes.");
      }
      setBateria(bateria);
      setPlan({ ...sesion, micCheckHecho: bateria.micCheckAt !== null });
      setFase("jugando");
    } catch (e) {
      setError(mensajeDeError(e));
      setFase("prep");
    }
  }

  if (fase === "jugando" && bateria && plan) {
    return (
      <PeffPlayer
        bateria={bateria}
        plan={plan}
        banda={bateria.banda}
        onExit={() => navigate(exitTo)}
        onDone={(bateriaId) => navigate(doneTo(bateriaId))}
      />
    );
  }

  if (fase === "cargando") return <Loader label={t("common.loading")} />;

  return (
    <AdultScreen>
      <AdultHeader title={t("eval.prep.title")} />
      <Content>
        {error ? <ErrorBanner message={error} /> : null}
        <Card>
          <h2>Prepara el espacio 🛋️</h2>
          <div className={styles.checklist} style={{ marginTop: 10 }}>
            <div className={styles.check}>
              <div className={styles.ico}>🪑</div>
              <div>
                <b>Lugar cómodo y sin distractores</b>
                <span>Siéntate con el peque frente a la pantalla, lejos de ruido.</span>
              </div>
            </div>
            <div className={styles.check}>
              <div className={styles.ico}>🤫</div>
              <div>
                <b>El adulto no modela ni corrige</b>
                <span>El modelo lo da la app. Acompaña en silencio: así el resultado es válido.</span>
              </div>
            </div>
            <div className={styles.check}>
              <div className={styles.ico}>🎙️</div>
              <div>
                <b>Micrófono</b>
                <span>{t("eval.prep.mic")}</span>
              </div>
            </div>
          </div>
        </Card>
        {qaHabilitado ? (
          <label
            style={{
              display: "flex", alignItems: "center", gap: 8, margin: "4px 2px",
              fontSize: 13, color: "var(--ink-soft)", cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={modoPrueba}
              onChange={(e) => setModoPrueba(e.target.checked)}
            />
            {t("eval.prep.qaMode")}
          </label>
        ) : null}
        <Button variant="primary" block onClick={() => void comenzar()}>
          {t("eval.prep.start")}
        </Button>
        <Link to={backTo} style={{ textAlign: "center" }}>
          {t("common.back")}
        </Link>
      </Content>
    </AdultScreen>
  );
}

/**
 * Resuelve la batería a administrar: retoma la que esté en curso (la más
 * reciente) o, si no hay ninguna, crea una nueva. El bug de banda A era crear
 * SIEMPRE una nueva: la 2.ª sesión nunca caía sobre la batería de la 1.ª, que
 * quedaba para siempre en 'en_progreso' y nunca se procesaba.
 *
 * La reanudación es POR MODO: solo se retoma una batería del MISMO modo que el
 * pedido (QA vs clínica normal). Si se pide QA y solo hay una normal en curso
 * (banda A con la 2.ª sesión 'en_progreso'), NO se retoma esa —plan completo,
 * F4=69— sino que se crea una nueva de PRUEBA (reducida). Antes el retomar
 * ignoraba el toggle y administraba la batería completa.
 */
export async function resolverBateria(
  childId: string,
  modoPrueba = false,
): Promise<BateriaCompletaDTO> {
  const historial = await bateriaApi.listar(childId);
  const enCurso = bateriaReanudable(historial, modoPrueba);
  // No hay ninguna EN CURSO del modo pedido → se crea una nueva en ese modo.
  if (!enCurso) return bateriaApi.crear({ childId, ...(modoPrueba ? { modoPrueba: true } : {}) });
  // Una batería suspendida vuelve a 'en_progreso' antes de administrarse.
  if (enCurso.estado === "suspendida") await bateriaApi.reanudar(enCurso.id);
  return bateriaApi.obtener(enCurso.id);
}
